import { createFileRoute } from "@tanstack/react-router";
import { LocalServicePage } from "@/components/LocalServicePage";
import hero from "@/assets/smart-home.webp";
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { localBusinessSchema, NEIGHBORHOOD_GEO } from "@/lib/localBusinessSchema";

const NEIGHBORHOOD = "Cherry Creek";
const SLUG = "cherry-creek";
const ZIP = "80206";
const URL = "https://apexav.com/cherry-creek/smart-home-installation";

const faqs = [
  {
    q: "How much does smart home installation cost in Cherry Creek?",
    a: "Cherry Creek smart home projects typically start around $15,000 for a single-system install (lighting OR shading OR audio). Whole-home integrated systems for Hilltop and Polo Club estates — lighting, shading, climate, audio, video, security, and access on one interface — generally run $50,000–$250,000+ depending on home size and finish detail. Every project is fixed-bid up front.",
  },
  {
    q: "Which smart home platform is best for a Cherry Creek home?",
    a: "It depends on what you want and how much you want to be involved. For lighting-and-shading-only homes we typically spec leading wireless retrofit platforms. For full whole-home automation (lighting, AV, climate, security on one app), we recommend industry-leading control systems that reliably handle 50+ device integrations. Fully custom control platforms are excellent for ultra-luxury custom UIs. We are certified dealers for multiple platforms and will recommend based on your actual needs, not what we sell most of.",
  },
  {
    q: "Can you retrofit a smart home into an existing Cherry Creek house without tearing up walls?",
    a: "Yes. Wireless platforms retrofit cleanly into 1920s Hilltop homes and Cherry Creek North townhomes — every existing switch is replaced one-for-one with a smart keypad, no wall damage. Wired systems can also be added during a remodel or by snaking low-voltage cabling through accessible chases. We always survey before quoting so you know exactly what's possible without demo.",
  },
  {
    q: "Will the system still work if my internet goes down?",
    a: "Yes — that's a critical design decision. Lighting, shading, climate, locks, and most AV scenes run on a local processor inside your home, not in the cloud. Internet only affects remote access from your phone when you're away. We also install enterprise-grade Cherry Creek networking (UniFi, Araknis, or Pakedge) with cellular failover for homes that can't tolerate any downtime.",
  },
  {
    q: "Do you serve Cherry Creek North townhomes and condos, or only single-family homes?",
    a: "Both. Cherry Creek North townhomes and condos are some of our most common projects — a wireless lighting system plus a Sonos or in-wall audio install can be completed in 2–4 days with zero construction. We also handle full HOA documentation if your building requires it.",
  },
];

const testimonials = [
  { quote: "Apex installed whole-home lighting control across our entire Hilltop estate — every keypad, every shade, every scene. The programming is so dialed in I forgot what light switches used to feel like.", name: "James W.", location: "Hilltop, Cherry Creek" },
  { quote: "We had three legacy systems in our Polo Club home — separate lighting, Sonos audio, Nest thermostats. Apex unified everything onto one platform. One app, one remote, finally.", name: "Catherine M.", location: "Polo Club" },
  { quote: "Did a wireless lighting retrofit in our 1925 Country Club home. Zero wall damage, every switch is now a smart keypad, and the scenes are perfect.", name: "Robert H.", location: "Country Club" },
];

export const Route = createFileRoute("/cherry-creek/smart-home-installation")({
  head: () => ({
    meta: [
      { title: "Smart Home Installation Cherry Creek | Apex AV Denver" },
      { name: "description", content: "Custom smart home automation in Cherry Creek, Hilltop, and Polo Club. Certified smart home integrator. Free in-home consultation." },
      { name: "keywords", content: "smart home installation Cherry Creek, smart home automation Cherry Creek, Hilltop smart home, Polo Club home automation, lighting automation Cherry Creek" },
      { property: "og:title", content: "Smart Home Installation Cherry Creek — Apex AV" },
      { property: "og:description", content: "Custom whole-home automation in Cherry Creek, Hilltop, and Polo Club." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { name: "twitter:title", content: "Smart Home Installation Cherry Creek — Apex AV" },
      { name: "twitter:description", content: "Custom whole-home automation in Cherry Creek, Hilltop, and Polo Club." },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Smart Home Installation in ${NEIGHBORHOOD}`,
              description: `Whole-home smart home automation in ${NEIGHBORHOOD}, Denver, CO.`,
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "Place", name: `${NEIGHBORHOOD}, Denver, CO ${ZIP}` },
              serviceType: "Smart Home Automation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "15000" },
            },
            localBusinessSchema({
              neighborhood: NEIGHBORHOOD,
              url: URL,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              zip: ZIP,
              latitude: NEIGHBORHOOD_GEO[SLUG].latitude,
              longitude: NEIGHBORHOOD_GEO[SLUG].longitude,
              priceFloor: 15000,
              serviceName: "Smart Home Installation",
            }),
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LocalServicePage
      neighborhood={NEIGHBORHOOD}
      neighborhoodSlug={SLUG}
      zip={ZIP}
      service="smart home installation"
      serviceKey="smart-home"
      parentServiceLabel="smart home automation"
      parentServiceTo="/services/smart-home"
      h1="Smart Home Installation in Cherry Creek, CO"
      intro="Whole-home automation engineered for Cherry Creek estates, townhomes, and historic Country Club residences. Certified smart home integrator. One interface for every system in your home — lights, shades, climate, audio, video, security, and access."
      hero={hero}
      bullets={[
        "Certified smart home integrator",
        "Wireless retrofit for Cherry Creek historic homes",
        "Whole-home lighting & motorized shades",
        "Voice control via Alexa, Google, Apple Home",
        "Enterprise-grade networking with cellular failover",
        "Local programming — works without internet",
      ]}
      microNeighborhoods={["Hilltop", "Polo Club", "Cherry Creek North", "Country Club", "Belcaro", "Cherry Creek East"]}
      testimonials={testimonials}
      faqs={faqs}
      bodyParagraphs={[
        {
          heading: "The right platform for your Cherry Creek home",
          body: (
            <>
              <p>
                Cherry Creek smart home projects fall into three buckets: <strong>lighting-and-shading-only</strong> retrofits in Country Club and Hilltop historic homes (leading wireless platforms), <strong>whole-home automation</strong> in Polo Club estates and new builds (industry-leading control systems), and <strong>ultra-luxury custom integration</strong> in larger estate properties (fully custom platforms with tailored interfaces). We're certified dealers for multiple platforms, which means we recommend based on your actual house and needs — not on what we have to move.
              </p>
              <p>
                Every Cherry Creek project starts with a free in-home walkthrough. We map your existing systems, photograph every switch and outlet, and deliver a fixed-scope proposal with drawings, equipment list, and price — usually within a week.
              </p>
            </>
          ),
        },
        {
          heading: "Wireless lighting — the Cherry Creek historic home special",
          body: (
            <>
              <p>
                Cherry Creek's 1920s and 1930s estates in Hilltop, Country Club, and Belcaro have plaster walls, original millwork, and details we will not damage. Leading <strong>wireless lighting platforms</strong> are built for exactly this — every existing toggle switch is swapped one-for-one with a smart keypad, no wall demolition, no rewiring. The system runs on a local processor (no cloud dependency), supports remotes that mount on any surface, and integrates with motorized shades, thermostats, and audio without touching the wiring.
              </p>
              <p>
                Most Cherry Creek wireless lighting retrofits — even 5,000+ sqft estates — wrap in 3–7 days with zero construction.
              </p>
            </>
          ),
        },
        {
          heading: "Whole-home automation for Polo Club & new builds",
          body: (
            <>
              <p>
                For Polo Club estates, new construction, and major remodels, we recommend industry-leading whole-home platforms. They handle 50+ device integrations reliably (smart lighting, Sonos audio, Sony or JVC projectors, ecobee thermostats, Ring or DoorBird intercoms, Yale or Schlage locks, Hikvision or Luma cameras), run everything off a local processor, and give you one app, one remote, and customizable on-wall touchscreens.
              </p>
              <p>
                We pair every install with <strong>enterprise-grade Cherry Creek networking</strong> (UniFi or Araknis) and a UPS-backed equipment rack so the system stays up through power flickers and ISP outages. Cellular failover is available for homes that can't tolerate any downtime.
              </p>
            </>
          ),
        },
        {
          heading: "Add a theater to your Cherry Creek smart home",
          body: (
            <>
              <p>
                Most Cherry Creek smart home clients add a dedicated theater or media room within 12 months. Because the control system is already in place, the theater becomes another zone — one button drops the screen, dims lights, drops the shades, and fires the projector. See our <a href="/cherry-creek/home-theater-installation" className="text-brand-text underline underline-offset-2">Cherry Creek home theater installation</a> page for design and pricing.
              </p>
            </>
          ),
        },
      ]}
    />
  ),
});
